import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Money } from '../ui/Money';
import { Plane, TrendingUp, TrendingDown, Check, Search } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Manually add a company-paid ticket record for any employee — including PAST
// records (backfill) so the yearly cost comparison has history.
export const AddTicketRecordDialog = ({ open, onOpenChange, onSaved }) => {
  const [employees, setEmployees] = useState([]);
  const [empSearch, setEmpSearch] = useState('');
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [form, setForm] = useState({
    issue_due_date: '', actual_issue_date: '', approved_fare: '', actual_fare: '', remarks: '', reference_date: '',
  });
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (open) {
      axios.get(`${API}/api/employees`, { headers })
        .then((r) => setEmployees(Array.isArray(r.data) ? r.data : []))
        .catch(() => setEmployees([]));
      // reset
      setSelectedEmp(null); setEmpSearch('');
      setForm({ issue_due_date: '', actual_issue_date: '', approved_fare: '', actual_fare: '', remarks: '', reference_date: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const filtered = useMemo(() => {
    const q = empSearch.trim().toLowerCase();
    if (!q) return employees.slice(0, 50);
    return employees.filter((e) =>
      (e.name || '').toLowerCase().includes(q) || (e.employee_code || '').toLowerCase().includes(q)
    ).slice(0, 50);
  }, [empSearch, employees]);

  const approved = form.approved_fare === '' ? null : Number(form.approved_fare);
  const actual = form.actual_fare === '' ? null : Number(form.actual_fare);
  const diff = approved != null && actual != null ? approved - actual : null;

  const handleSave = async () => {
    if (!selectedEmp) {
      toast({ title: 'Select an employee', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/api/ticket-tracker/record`, {
        employee_id: selectedEmp.id,
        issue_due_date: form.issue_due_date || null,
        actual_issue_date: form.actual_issue_date || null,
        approved_fare: form.approved_fare === '' ? null : Number(form.approved_fare),
        actual_fare: form.actual_fare === '' ? null : Number(form.actual_fare),
        remarks: form.remarks || null,
        reference_date: form.reference_date || null,
      }, { headers });
      toast({ title: 'Ticket record added', description: form.actual_issue_date ? 'Recorded as issued.' : 'Recorded as pending.' });
      onOpenChange(false);
      onSaved && onSaved(res.data);
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Could not add record', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="add-ticket-record-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plane className="h-5 w-5 text-sky-600" /> Add Ticket Record
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Employee picker */}
          {selectedEmp ? (
            <div className="flex items-center justify-between rounded-lg border border-sky-200 bg-sky-50 px-3 py-2" data-testid="selected-employee">
              <div className="text-sm">
                <span className="font-medium text-gray-900">{selectedEmp.name}</span>
                <span className="ml-2 font-mono text-xs text-gray-500">{selectedEmp.employee_code}</span>
                <span className="ml-2 text-xs text-gray-400">{selectedEmp.department}</span>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setSelectedEmp(null)} data-testid="change-employee">Change</Button>
            </div>
          ) : (
            <div>
              <Label className="text-xs">Employee</Label>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-gray-400" />
                <Input className="pl-8" placeholder="Search name or code…" value={empSearch} onChange={(e) => setEmpSearch(e.target.value)} data-testid="employee-search" />
              </div>
              <div className="mt-1 max-h-40 overflow-y-auto border rounded-lg divide-y">
                {filtered.length === 0 ? (
                  <p className="text-xs text-gray-400 p-3 text-center">No matches</p>
                ) : filtered.map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => setSelectedEmp(e)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between"
                    data-testid={`emp-option-${e.employee_code}`}
                  >
                    <span>{e.name} <span className="font-mono text-xs text-gray-400">{e.employee_code}</span></span>
                    <span className="text-xs text-gray-400">{e.department}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Date of Flying</Label>
              <Input type="date" value={form.issue_due_date} onChange={(e) => set('issue_due_date', e.target.value)} data-testid="add-issue-due-date" />
            </div>
            <div>
              <Label className="text-xs">Ticket Issue Date</Label>
              <Input type="date" value={form.actual_issue_date} onChange={(e) => set('actual_issue_date', e.target.value)} data-testid="add-actual-issue-date" />
            </div>
            <div>
              <Label className="text-xs">Company Approved Fare (AED)</Label>
              <Input type="number" min="0" step="0.01" placeholder="0.00" value={form.approved_fare} onChange={(e) => set('approved_fare', e.target.value)} data-testid="add-approved-fare" />
            </div>
            <div>
              <Label className="text-xs">Actual Ticket Fare (AED)</Label>
              <Input type="number" min="0" step="0.01" placeholder="0.00" value={form.actual_fare} onChange={(e) => set('actual_fare', e.target.value)} data-testid="add-actual-fare" />
            </div>
          </div>

          {diff != null && (
            <div className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm font-semibold ${diff > 0 ? 'bg-emerald-50 text-emerald-700' : diff < 0 ? 'bg-rose-50 text-rose-700' : 'bg-gray-50 text-gray-600'}`} data-testid="add-fare-comparison">
              <span className="flex items-center gap-1">
                {diff > 0 ? <TrendingDown className="h-4 w-4" /> : diff < 0 ? <TrendingUp className="h-4 w-4" /> : null}
                {diff > 0 ? 'Saved vs budget' : diff < 0 ? 'Over budget' : 'On budget'}
              </span>
              <span className="inline-flex items-center gap-1">{diff > 0 ? '+' : diff < 0 ? '-' : ''}<Money value={Math.abs(diff)} digits={2} /></span>
            </div>
          )}

          <div>
            <Label className="text-xs">Remarks / Comments</Label>
            <Textarea rows={2} value={form.remarks} onChange={(e) => set('remarks', e.target.value)} placeholder="Optional notes…" data-testid="add-remarks" />
          </div>
          <p className="text-[11px] text-gray-400">
            Tip: to backfill a past record for year-on-year comparison, set the Actual Ticket Issue Date to a date in that year.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="add-record-cancel">Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !selectedEmp} data-testid="add-record-save">
            <Check className="h-4 w-4 mr-1" /> {saving ? 'Saving…' : 'Add Record'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddTicketRecordDialog;
